import type { ShaderMaterial } from 'three';
import type { PaletteModel } from '../../data/types';
import type {
  StyleParams,
  MaterialFactory,
  PaintedMaterialOptions,
  PainterProjectionState,
} from '../types';
import {
  createSharedUniforms,
  updateSharedUniforms,
  updatePainterProjection,
  SharedUniforms,
} from './sharedUniforms';
import { createPaintedMaterial } from './paintedMaterial';
import { createWaterMaterial } from './waterMaterial';
import { createShoreUniforms, disposeShoreTextures, setShoreGrid } from './shoreUniforms';
import { createWaterCoverUniforms, setWaterCovers } from './waterCoverUniforms';
import { createSkyMaterial } from './skyMaterial';
import { createGlowMaterial } from '../night/glowLayer';
import { registerShaderChunks, isChunksRegistered } from '../shaders/registerChunks';
import { createPatternUniforms } from '../patterns/packPatterns';
import { paintSectorPx } from '../patterns/lineCover';
import { citizenPoseTexture } from '../citizenPoses';
import { PATTERN_PRESETS } from '../patterns/presets';
import type { ShoreGrid } from '../shoreGrid';
import type { WaterCover } from '../waterCover';

interface MaterialCache {
  painted: Map<string, ShaderMaterial>;
  water: ShaderMaterial | null;
  sky: ShaderMaterial | null;
  glow: ShaderMaterial | null;
}

function optionsKey(options: PaintedMaterialOptions): string {
  return `${options.instanced ? 'i' : ''}${options.doubleSided ? 'd' : ''}${options.depthOnly ? 'm' : ''}${options.clothWind ? 'w' : ''}${options.hearthSmoke ? 's' : ''}`;
}

export function createMaterialFactory(palette: PaletteModel, style: StyleParams): MaterialFactory {
  if (!isChunksRegistered()) {
    registerShaderChunks();
  }

  const sharedUniforms: SharedUniforms = createSharedUniforms(style, palette);
  const patternUniforms = createPatternUniforms(PATTERN_PRESETS, palette);
  const shoreUniforms = createShoreUniforms();
  const coverUniforms = createWaterCoverUniforms();
  const cache: MaterialCache = {
    painted: new Map(),
    water: null,
    sky: null,
    glow: null,
  };

  const allMaterials: ShaderMaterial[] = [];

  return {
    painted(options: PaintedMaterialOptions = {}): ShaderMaterial {
      const key = optionsKey(options);
      let material = cache.painted.get(key);
      if (!material) {
        material = createPaintedMaterial(sharedUniforms, patternUniforms, options);
        cache.painted.set(key, material);
        allMaterials.push(material);
      }
      return material;
    },

    water(): ShaderMaterial {
      if (!cache.water) {
        cache.water = createWaterMaterial(sharedUniforms, shoreUniforms, coverUniforms);
        allMaterials.push(cache.water);
      }
      return cache.water;
    },

    sky(): ShaderMaterial {
      if (!cache.sky) {
        cache.sky = createSkyMaterial(sharedUniforms);
        allMaterials.push(cache.sky);
      }
      return cache.sky;
    },

    glow(): ShaderMaterial {
      if (!cache.glow) {
        cache.glow = createGlowMaterial(sharedUniforms);
        allMaterials.push(cache.glow);
      }
      return cache.glow;
    },

    update(timeSeconds: number): void {
      updateSharedUniforms(sharedUniforms, timeSeconds);
    },

    setSunDirection(direction): void {
      sharedUniforms.uSunDirection.value.set(...direction).normalize();
    },

    setPainterProjection(state: PainterProjectionState): void {
      updatePainterProjection(sharedUniforms, state);
    },

    setRenderHeight(heightPx: number): void {
      sharedUniforms.uRenderHeight.value = heightPx;
    },

    get renderHeight(): number {
      return sharedUniforms.uRenderHeight.value;
    },

    setPaintRadius(kuwaharaRadius: number): void {
      patternUniforms.uPaintSectorPx.value = paintSectorPx(kuwaharaRadius);
    },

    setShoreGrid(grid: ShoreGrid): void {
      setShoreGrid(shoreUniforms, grid);
    },

    setWaterCovers(covers: readonly WaterCover[]): void {
      setWaterCovers(coverUniforms, covers);
    },

    setCitizenPoses(poses: Float32Array, bones: number): void {
      // The cast and the crowd each hand over the one table they share.
      if (sharedUniforms.uCitizenPoses.value?.image.data === poses) return;
      sharedUniforms.uCitizenPoses.value?.dispose();
      sharedUniforms.uCitizenPoses.value = citizenPoseTexture(poses, bones);
    },

    dispose(): void {
      for (const material of allMaterials) {
        material.dispose();
      }
      sharedUniforms.uFieldColourMap.value.dispose();
      sharedUniforms.uPainterWarpField.value.dispose();
      sharedUniforms.uPainterWarpLookup.value.dispose();
      sharedUniforms.uCitizenPoses.value?.dispose();
      sharedUniforms.uCitizenPoses.value = null;
      disposeShoreTextures(shoreUniforms);
      cache.painted.clear();
      cache.water = null;
      cache.sky = null;
      cache.glow = null;
      allMaterials.length = 0;
    },
  };
}
